'use client'

import { algoliaProductObjectId } from '@repo/algolia/config'
import { getAlgoliaQueryInfo } from '@repo/algolia/query'
import { mapProductToElevarAddToCartData } from '@repo/elevar/mapper'
import { SHOPIFY_ATTRIBUTES } from '@repo/shopify/attributes'
import { Attribute, Product } from '@repo/shopify/types'
import { getFromStorage, LOCAL_STORAGE_LIST } from '@repo/storage/service'
import { Button } from '@repo/ui/components/button'
import { FunctionComponent, useActionState, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { addCartItem as addCartItemAction } from '../../../actions/cart.actions'
import { useCart } from '../../../providers/CartProvider'
import { useProduct } from '../../../providers/ProductProvider'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { useTelemetry } from '../../../providers/TelemetryProvider'
import { PageDto } from '../../../types/page.types'
import { ProductDto } from '../../../types/product.types'
import ProductGiftCardSheet from './ProductGiftCardSheet'

interface SubmitButtonProps {
  pageDto: PageDto
  productDto: ProductDto
  productAvailableForSale: boolean
  variantAvailableForSale: boolean | undefined
  selectedVariantId: string | undefined
}

const SubmitButton: FunctionComponent<SubmitButtonProps> = ({ pageDto, productDto, productAvailableForSale, variantAvailableForSale, selectedVariantId }) => {
  const { openSheet } = useSheet()
  const { addToCartRef } = useProduct()
  const isGiftCard = productDto?.sanityProduct?.type === 'giftCard'

  if (!productAvailableForSale || (selectedVariantId && !variantAvailableForSale)) {
    return (
      <Button type='button' disabled={true} variant={selectedVariantId ? 'defaultAuto' : 'secondaryAuto'}>
        {pageDto.dictionary?.product?.outofstock || 'Out of stock'}
      </Button>
    )
  }

  return (
    <div ref={addToCartRef}>
      <Button
        variant='defaultAuto'
        disabled={!selectedVariantId}
        type={isGiftCard ? 'button' : 'submit'}
        onClick={() => isGiftCard && openSheet(SHEET.GIFT_CARD)}
        aria-label={selectedVariantId ? pageDto.dictionary?.product?.addtobag || 'Add to bag' : pageDto.dictionary?.product?.pleaseselectanoption || 'Please select an option'}
      >
        {isGiftCard && <>{pageDto.dictionary?.product?.confirm || 'Confirm'}</>}
        {!isGiftCard && <>{pageDto.dictionary?.product?.addtobag || 'Add to bag'}</>}
      </Button>
    </div>
  )
}

interface Props {
  pageDto: PageDto
  productDto: ProductDto
  shopifyProduct: Product
}

const ProductAddToCart: FunctionComponent<Props> = ({ pageDto, productDto, shopifyProduct }) => {
  const { addCartItem } = useCart()
  const { getSelectedVariant } = useProduct()
  const ref = useRef<HTMLFormElement>(null)
  const [additionalAttributes, setAdditionalAttributes] = useState<Attribute[]>([])
  const [state, formAction, pending] = useActionState(addCartItemAction, null)
  const { trackElevarAddToCartEvent, trackAlgoliaAddToCartEvent, trackAlgoliaAddToCartAfterSearchEvent } = useTelemetry()

  // Determine variant
  const variant = getSelectedVariant(shopifyProduct)
  const defaultVariantId = shopifyProduct.variants.length === 1 ? shopifyProduct.variants[0]?.id : undefined
  const selectedVariantId = variant?.id || defaultVariantId
  const finalVariant = shopifyProduct.variants.find((variant) => variant.id === selectedVariantId)

  // Determine the list the product was taken from (assume the most recently viewed collection)
  const list = getFromStorage(LOCAL_STORAGE_LIST)

  // Determine Algolia data
  const algoliaObjectId = algoliaProductObjectId(shopifyProduct.handle)
  const algoliaQueryInfo = getAlgoliaQueryInfo(algoliaObjectId)

  // Line item attributes
  const lineItemAttributes = [
    ...additionalAttributes,
    ...(list ? [{ key: SHOPIFY_ATTRIBUTES.LIST, value: String(list) }] : []),
    ...(algoliaQueryInfo ? [{ key: SHOPIFY_ATTRIBUTES.ALGOLIA_QUERY_ID, value: String(algoliaQueryInfo.queryID) }] : []),
  ]

  // Payload for server action
  const payload = { shopifyInstance: pageDto.shopifyInstance, selectedVariantId, attributes: lineItemAttributes.length > 0 ? lineItemAttributes : undefined }
  const actionWithVariant = formAction.bind(null, payload)

  useEffect(() => {
    if (!pending && state?.status === 'error') {
      toast.error(state.message)
    }

    if (!pending && finalVariant && state?.status === 'success') {
      trackElevarAddToCartEvent(mapProductToElevarAddToCartData(shopifyProduct, finalVariant, pageDto.params.locale))

      if (algoliaQueryInfo) {
        trackAlgoliaAddToCartAfterSearchEvent({
          eventName: 'Add To Cart',
          objectIDs: [algoliaObjectId],
          queryID: algoliaQueryInfo.queryID,
        })
      } else {
        trackAlgoliaAddToCartEvent({
          eventName: 'Add To Cart',
          objectIDs: [algoliaObjectId],
        })
      }
    }

    // Do not provide all deps to risk multiple telemetry events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, pending])

  return (
    <>
      <form
        ref={ref}
        action={async () => {
          addCartItem(finalVariant!, shopifyProduct)
          actionWithVariant()
        }}
      >
        <SubmitButton
          pageDto={pageDto}
          productDto={productDto}
          productAvailableForSale={shopifyProduct.availableForSale}
          variantAvailableForSale={finalVariant?.availableForSale}
          selectedVariantId={selectedVariantId}
        />
      </form>
      <ProductGiftCardSheet pageDto={pageDto} form={ref} setAdditionalAttributes={setAdditionalAttributes} />
    </>
  )
}

export default ProductAddToCart
